/* eslint-disable dot-notation */
/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/prefer-for-of */
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import getAppState from 'app-state';
import { useLocation } from 'react-router-dom';
import {
  setState,
  setError,
  setLoading,
  setSnackbar,
  setIsPws,
} from './autoshipSlice';
import { selectWholeState, selectSnackbar, selectIsLoading } from './selectors';
import objectDeepCompare from 'object-deep-compare';

const useFetchAutoship = () => {
  const [autoship, setAutoship] = useState<StateData | unknown>({});
  const dispatch = useDispatch();
  const locale = getAppState('rcmi18n');
  const isPws = !!getAppState('pws');
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const CompareValuesWithConflicts =
    objectDeepCompare.CompareValuesWithConflicts;

  /**
   * I'd much rather find a way to export the getState function
   * provided by redux "store" but currently cant find an easy
   * way to do that. For now, we'll use a selector to use in
   * the "saveAutoship" function.
   */
  const activeState = useSelector(selectWholeState);
  const currentSnackBar = useSelector(selectSnackbar);
  const isLoading = useSelector(selectIsLoading);

  const getStoreItems = async () => {
    const response = await axios.get(
      '/api/v2/get-items/' + locale['defaultLocale']
    );
    return response.data;
  };

  const getAutoship = async (allStoreItems) => {
    const autoshipId = params.get('as_id');
    const currentUser = getAppState('currentUser.userId');
    const body = { userId: currentUser };
    const response = await axios.post(
      `/api/v2/get-autoship/${autoshipId}`,
      body
    );
    const { items, userId } = response.data;
    if (currentUser != userId) {
      dispatch(
        setError(
          'You are not authorized to view this page. Please contact Support for further assistance.'
        )
      );
      dispatch(setSnackbar({ message: 'Unauthorized', type: 'error' }));
      return null;
    }
    const orderItems = items
      ?.map((item) => {
        // match item with item from store
        const foundItem = allStoreItems.find(
          (storeItem) => storeItem.itemCode === item.itemCode
        );
        if (foundItem)
          return {
            ...foundItem,
            pv: foundItem.data?.pv,
            bv: foundItem.data?.bv,
            quantity: item.quantity,
          };
      })
      .sort((a, b) => b.itemCode - a.itemCode)
      .filter((item) => item !== undefined && item !== null);

    return { ...response.data, items: orderItems };
  };

  const getData = async () => {
    try {
      dispatch(setLoading(true))
      const storeItems = await getStoreItems();
      const initialAutoship = await getAutoship(storeItems);
      setAutoship(initialAutoship);
      dispatch(setState(initialAutoship));
      dispatch(setLoading(false))
      dispatch(setIsPws(isPws));
    } catch (e) {
      dispatch(
        setError(
          e.response?.data?.error?.message ??
            'There was an error getting Autoship data.'
        )
      );
    }
  };

  const changesDetected = useMemo(() => {
    if (!autoship || !activeState) return false;
    return (
      CompareValuesWithConflicts(
        autoship['autoshipDetails'],
        activeState.autoshipDetails
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship?.['billingInfo']?.['billingAddress'],
        activeState.billingInfo.billingAddress
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship?.['billingInfo']?.['cardInfo']?.['nameOnCard'],
        activeState.billingInfo.cardInfo.nameOnCard
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship?.['billingInfo']?.['cardInfo']?.['cardNumber']?.slice(-4),
        activeState.billingInfo?.cardInfo?.cardNumber?.slice(-4)
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship?.['billingInfo']?.['cardInfo']?.['expirationMonth'],
        activeState.billingInfo.cardInfo.expirationMonth
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship?.['billingInfo']?.['cardInfo']?.['expirationYear'],
        activeState.billingInfo.cardInfo.expirationYear
      )?.length >= 1 ||
      CompareValuesWithConflicts(
        autoship['shippingAddress'],
        activeState.shippingAddress
      )?.length >= 1 ||
      CompareValuesWithConflicts(autoship['items'], activeState.items)
        ?.length >= 1
    );
  }, [autoship, activeState]);

  const saveAutoship = async () => {
    const sameAsShipping = Boolean(
      activeState.billingInfo?.billingAddress?.sameAsShipping
    );
    const currentState = {
      ...activeState,
      billingInfo: {
        ...activeState.billingInfo,
        billingAddress: {
          sameAsShipping,
          ...(sameAsShipping
            ? activeState.shippingAddress
            : activeState.billingInfo.billingAddress),
        },
      },
    };
    try {
      await axios.post('/api/v2/update-autoship', currentState);

      const newState = {
        ...currentState,
        error: '',
      };

      dispatch(setState(newState));
      dispatch(setSnackbar({ message: 'Autoship saved!' }));
      return;
    } catch (e) {
      dispatch(
        setSnackbar({ message: 'Autoship failed to save.', type: 'error' })
      );
      dispatch(
        setError(
          e.response?.data?.error?.message ??
            'Unable to save Autoship. Please try again.'
        )
      );
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!autoship || !activeState) return;

    const changesWarning =
      'You have unsaved changes. Please save or discard them.';

    if (!isLoading && changesDetected && !currentSnackBar.message.length) {
      dispatch(
        setSnackbar({
          type: 'warning',
          message: changesWarning,
          delay: 600000,
        })
      );
    }

    if (
      currentSnackBar.message === changesWarning &&
      !isLoading &&
      !changesDetected
    ) {
      dispatch(
        setSnackbar({
          type: 'warning',
          message: '',
          delay: 0,
        })
      );
    }
  }, [changesDetected, autoship, activeState, currentSnackBar, isLoading]);

  const revertState = () => {
    dispatch(setState(autoship));
  };

  /**
   * autoship: the autoship response object from the api call
   * getAutoship: Function to make api call to fetch autoship and hydrate redux
   * saveAutoship: Function that sends ACTIVE redux state to backend to save, and re-hydrate redux
   * revertState: set redux to saved autoship state
   */
  return {
    autoship,
    changesDetected,
    getAutoship: getData,
    saveAutoship,
    revertState,
  };
};

export default useFetchAutoship;
